import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtCurrency, fmtDateShort, API_BASE } from "@/lib/api";
import { StoreFilter, getActiveStore } from "@/components/StoreFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Download, Mail, Plus, Wallet, TrendingUp, TrendingDown, Landmark, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import DailyEmailCard from "@/components/DailyEmailCard";
import StoreRollup from "@/components/StoreRollup";
import PayoutBreakdown from "@/components/PayoutBreakdown";

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "payouts"
    ? "payouts"
    : searchParams.get("tab") === "daily" ? "daily" : "transactions";

  const [start, setStart] = useState(monthAgo);
  const [end, setEnd] = useState(today);
  const [store, setStore] = useState(getActiveStore());
  const [tx, setTx] = useState(null);
  const [daily, setDaily] = useState(null);
  const [dailyDate, setDailyDate] = useState(today);

  // expense dialog
  const [expOpen, setExpOpen] = useState(false);
  const [expDate, setExpDate] = useState(today);
  const [expCat, setExpCat] = useState("Paint Shop Fee");
  const [expDesc, setExpDesc] = useState("");
  const [expAmt, setExpAmt] = useState("");

  const loadTx = async () => {
    const params = { start, end };
    if (store !== "all") params.store = store;
    const { data } = await api.get("/reports/transactions", { params });
    setTx(data);
  };
  const loadDaily = async () => {
    const params = { d: dailyDate };
    if (store !== "all") params.store = store;
    const { data } = await api.get("/reports/daily", { params });
    setDaily(data);
  };
  useEffect(() => { loadTx(); }, [start, end, store]);
  useEffect(() => { loadDaily(); }, [dailyDate, store]);

  const downloadCsv = () => {
    const storeQ = store !== "all" ? `&store=${encodeURIComponent(store)}` : "";
    const url = `${API_BASE}/reports/transactions.csv?start=${start}&end=${end}${storeQ}`;
    window.open(url, "_blank");
  };

  const emailSnapshot = () => {
    if (!daily) return;
    const subj = `Cabinet Depot — Daily Report ${daily.date}`;
    const body =
      `Daily report for ${daily.date}\n\n` +
      `Revenue:   $${daily.summary.revenue.toFixed(2)}\n` +
      `COGS:      $${daily.summary.cogs.toFixed(2)}\n` +
      `Expenses:  $${daily.summary.expenses.toFixed(2)}\n` +
      `Profit:    $${daily.summary.profit.toFixed(2)}\n` +
      `Orders:    ${daily.summary.orders}\n` +
      `Deliveries scheduled: ${daily.summary.deliveries_scheduled}\n`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
  };

  const submitExpense = async () => {
    if (!expAmt) { toast.error("Amount required"); return; }
    try {
      await api.post("/expenses", {
        date: expDate,
        category: expCat,
        description: expDesc || expCat,
        amount: parseFloat(expAmt),
      });
      toast.success("Expense logged");
      setExpOpen(false);
      setExpAmt("");
      setExpDesc("");
      await loadTx();
      await loadDaily();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] animate-fade-in">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Reports</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Financial reports
          </h1>
          <p className="text-muted-foreground mt-1">
            {store === "all"
              ? "Transactions, daily P&L, and category breakdowns."
              : `Filtered to ${store} store.`}
          </p>
        </div>
        <div className="flex gap-2">
          <StoreFilter value={store} onChange={setStore} testid="reports-store-filter" />
          <Dialog open={expOpen} onOpenChange={setExpOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-2" />Log expense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Log expense</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)} data-testid="exp-date" />
                </div>
                <div>
                  <Label>Amount</Label>
                  <Input type="number" step="0.01" value={expAmt} onChange={(e) => setExpAmt(e.target.value)} data-testid="exp-amount" />
                </div>
              </div>
              <div>
                <Label>Category</Label>
                <Input value={expCat} onChange={(e) => setExpCat(e.target.value)} data-testid="exp-category" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={expDesc} onChange={(e) => setExpDesc(e.target.value)} data-testid="exp-desc" />
              </div>
              <Button onClick={submitExpense} data-testid="exp-submit" className="w-full bg-primary hover:bg-[#203A2C]">
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily">Daily report</TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <div className="flex flex-wrap gap-3 mb-5 items-end">
            <div>
              <Label>From</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="tx-start" />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="tx-end" />
            </div>
            <Button variant="outline" onClick={downloadCsv} data-testid="export-csv">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
          </div>

          {tx && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <Stat label="Revenue" value={fmtCurrency(tx.summary.revenue)} />
                <Stat label="COGS" value={fmtCurrency(tx.summary.cogs)} />
                <Stat label="Expenses" value={fmtCurrency(tx.summary.expenses)} />
                <Stat label="Profit" value={fmtCurrency(tx.summary.profit)} accent />
                <Stat label="Margin" value={`${tx.summary.margin_pct}%`} />
              </div>

              <div className="surface-card p-6 mb-6">
                <div className="label-eyebrow mb-3">Profit trend</div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={tx.series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#2C4C3B" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="#2C4C3B" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E1DD" />
                      <XAxis dataKey="date" stroke="#63635E" fontSize={11} />
                      <YAxis stroke="#63635E" fontSize={11} tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        contentStyle={{ background: "#fff", border: "1px solid #E2E1DD", borderRadius: 6, fontSize: 12 }}
                        formatter={(v, name) => [fmtCurrency(v), name]}
                      />
                      <Area type="monotone" dataKey="profit" stroke="#2C4C3B" strokeWidth={2} fill="url(#g1)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="surface-card p-4 sm:p-6">
                <div className="overflow-x-auto -mx-4 sm:-mx-6">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                      <tr className="border-b border-border/70">
                        <th className="text-left font-medium px-4 sm:px-6 py-3">Date</th>
                        <th className="text-left font-medium px-4 py-3">Type</th>
                        <th className="text-left font-medium px-4 py-3">Reference</th>
                        <th className="text-left font-medium px-4 py-3">Description</th>
                        <th className="text-right font-medium px-4 py-3">Amount</th>
                        <th className="text-right font-medium px-4 sm:px-6 py-3">Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tx.rows.slice(0, 100).map((r, i) => (
                        <tr key={i} className="border-b border-border/40">
                          <td className="px-4 sm:px-6 py-2.5 num">{r.date}</td>
                          <td className="px-4 py-2.5">
                            <span className={`pill ${r.type === "sale" ? "bg-primary/10 text-primary border-primary/30" : "bg-[#D66853]/10 text-[#a44231] border-[#D66853]/30"}`}>
                              {r.type}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 font-mono text-xs">{r.ref}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{r.description}</td>
                          <td className="px-4 py-2.5 text-right num">{fmtCurrency(r.amount)}</td>
                          <td className="px-4 sm:px-6 py-2.5 text-right num font-semibold">{fmtCurrency(r.profit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="daily">
          <div className="mb-5">
            <DailyEmailCard />
          </div>

          <div className="mb-6">
            <StoreRollup defaultDate={dailyDate} />
          </div>

          <div className="flex flex-wrap gap-3 mb-5 items-end">
            <div>
              <Label>P&amp;L for date</Label>
              <Input type="date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)} data-testid="daily-date" />
            </div>
            <Button variant="outline" onClick={emailSnapshot} data-testid="email-daily">
              <Mail className="w-4 h-4 mr-2" /> Email via mail client
            </Button>
          </div>

          {daily && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat label="Revenue" value={fmtCurrency(daily.summary.revenue)} />
                <Stat label="COGS" value={fmtCurrency(daily.summary.cogs)} />
                <Stat label="Expenses" value={fmtCurrency(daily.summary.expenses)} />
                <Stat label="Profit" value={fmtCurrency(daily.summary.profit)} accent />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="surface-card p-6">
                  <div className="label-eyebrow mb-3">By category</div>
                  {Object.keys(daily.by_category).length === 0 ? (
                    <div className="text-sm text-muted-foreground">No sales on this day.</div>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(daily.by_category).map(([cat, v]) => (
                        <div key={cat} className="flex items-center justify-between border-b border-border/40 pb-2">
                          <div>
                            <div className="text-sm font-medium capitalize">{cat.replace("_", " ")}</div>
                            <div className="text-xs text-muted-foreground">{v.units} units</div>
                          </div>
                          <div className="text-right">
                            <div className="num font-semibold">{fmtCurrency(v.revenue)}</div>
                            <div className="text-xs text-muted-foreground">cost {fmtCurrency(v.cost)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="surface-card p-6">
                  <div className="label-eyebrow mb-3">Orders ({daily.orders.length})</div>
                  <div className="space-y-2">
                    {daily.orders.length === 0 && (
                      <div className="text-sm text-muted-foreground">No orders.</div>
                    )}
                    {daily.orders.map((o) => {
                      const t = o.items.reduce((s, it) => s + it.unit_price * it.qty, 0);
                      return (
                        <div key={o.id} className="flex items-center justify-between border-b border-border/40 pb-2">
                          <div>
                            <div className="text-sm font-mono">{o.number}</div>
                            <div className="text-xs text-muted-foreground">{o.customer.name}</div>
                          </div>
                          <div className="num font-semibold">{fmtCurrency(t)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="surface-card p-6 mt-6">
                <div className="label-eyebrow mb-3">Expenses logged on {fmtDateShort(daily.date)}</div>
                {daily.expenses.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No expenses logged.</div>
                ) : (
                  <div className="divide-y divide-border/40">
                    {daily.expenses.map((e) => (
                      <div key={e.id} className="flex items-center justify-between py-2">
                        <div>
                          <div className="text-sm font-medium">{e.category}</div>
                          <div className="text-xs text-muted-foreground">{e.description}</div>
                        </div>
                        <div className="num text-[#a44231]">-{fmtCurrency(e.amount)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="payouts">
          <PayoutsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="stat-card">
      <div className="label-eyebrow">{label}</div>
      <div className={`font-heading text-2xl font-bold tracking-tight num mt-2 ${accent ? "text-primary" : ""}`}>
        {value}
      </div>
    </div>
  );
}


function PayoutsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/stripe/payouts", { params: { limit: 20 } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-sm text-muted-foreground p-6">Loading…</div>;
  if (!data) return <div className="text-sm text-muted-foreground p-6">Stripe payouts unavailable. Check that your API key has the Payouts Read permission.</div>;

  const totalAvailable = (data.balance?.available || []).reduce((s, b) => s + b.amount, 0);
  const totalPending = (data.balance?.pending || []).reduce((s, b) => s + b.amount, 0);
  const upcomingTotal = (data.upcoming || []).reduce((s, p) => s + p.amount, 0);
  const nextArrival = data.pending_batches?.[0] || (data.upcoming?.[0]
    ? { available_on: new Date((data.upcoming[0].arrival_date || 0) * 1000).toISOString().slice(0, 10), net: data.upcoming[0].amount }
    : null);
  const nextArrivalDate = nextArrival
    ? new Date(nextArrival.available_on + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="stat-card" data-testid="stripe-available">
          <div className="flex items-start justify-between mb-2">
            <div className="label-eyebrow">Available now</div>
            <div className="w-8 h-8 rounded-md bg-secondary grid place-items-center text-primary">
              <Wallet className="w-4 h-4" strokeWidth={1.75} />
            </div>
          </div>
          <div className={`font-heading text-2xl font-bold num ${totalAvailable >= 0 ? "text-primary" : "text-[#a44231]"}`}>
            {fmtCurrency(totalAvailable / 100)}
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">Cleared, ready to pay out</div>
        </div>
        <div className="stat-card" data-testid="stripe-pending">
          <div className="flex items-start justify-between mb-2">
            <div className="label-eyebrow">Pending balance</div>
            <div className="w-8 h-8 rounded-md bg-secondary grid place-items-center text-primary">
              <TrendingUp className="w-4 h-4" strokeWidth={1.75} />
            </div>
          </div>
          <div className="font-heading text-2xl font-bold num">{fmtCurrency(totalPending / 100)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Charges settling (usually 2 business days)</div>
        </div>
        <div className="stat-card" data-testid="stripe-upcoming">
          <div className="flex items-start justify-between mb-2">
            <div className="label-eyebrow">Next arrival</div>
            <div className="w-8 h-8 rounded-md bg-secondary grid place-items-center text-primary">
              <Landmark className="w-4 h-4" strokeWidth={1.75} />
            </div>
          </div>
          {nextArrival ? (
            <>
              <div className="font-heading text-2xl font-bold num">
                {fmtCurrency((nextArrival.net || 0) / 100)}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Available {nextArrivalDate}
                {data.pending_batches?.length > 1 && ` · ${data.pending_batches.length - 1} more scheduled`}
              </div>
            </>
          ) : (
            <>
              <div className="font-heading text-2xl font-bold num">{fmtCurrency(upcomingTotal / 100)}</div>
              <div className="text-[11px] text-muted-foreground mt-1">Nothing scheduled yet</div>
            </>
          )}
        </div>
      </div>

      {data.pending_batches?.length > 0 && (
        <div className="surface-card p-6" data-testid="pending-batches">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-eyebrow">Settling to bank</div>
              <div className="text-xs text-muted-foreground mt-1">
                Pending charges grouped by the date they become payout-ready. Stripe usually pays them out within 1–2 business days after.
              </div>
            </div>
          </div>
          <div className="divide-y divide-border/60">
            {data.pending_batches.map((b) => (
              <ExpandableSettlingRow key={b.available_on} batch={b} />
            ))}
          </div>
        </div>
      )}

      {data.upcoming?.length > 0 && (
        <div className="surface-card p-6">
          <div className="label-eyebrow mb-4">On the way to your bank</div>
          <div className="divide-y divide-border/60">
            {data.upcoming.map((p) => (
              <PayoutRow key={p.id} p={p} highlight />
            ))}
          </div>
        </div>
      )}

      {data.failed?.length > 0 && (
        <div className="surface-card p-6 border-l-4 border-l-destructive">
          <div className="label-eyebrow mb-4 text-destructive">Failed payouts</div>
          <div className="divide-y divide-border/60">
            {data.failed.map((p) => (
              <PayoutRow key={p.id} p={p} failed />
            ))}
          </div>
        </div>
      )}

      <div className="surface-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="label-eyebrow">Recent payouts</div>
          <a
            href="https://dashboard.stripe.com/payouts"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary hover:underline"
          >
            View in Stripe →
          </a>
        </div>
        {data.recent?.length === 0 && (
          <div className="text-sm text-muted-foreground italic py-4">No recent payouts.</div>
        )}
        <div className="divide-y divide-border/60">
          {data.recent?.map((p) => (
            <ExpandablePayoutRow key={p.id} p={p} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ExpandableSettlingRow({ batch }) {
  const [open, setOpen] = useState(false);
  // batch.available_on is a YYYY-MM-DD string that already represents an ET calendar day.
  // Parse as local mid-day so the label doesn't shift under DST/TZ conversion.
  const [y, m, d] = batch.available_on.split("-").map(Number);
  const dt = new Date(y, m - 1, d, 12, 0, 0);
  const label = dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  return (
    <div data-testid={`settling-row-${batch.available_on}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between py-3 hover:bg-secondary/30 -mx-2 px-2 rounded transition-colors"
      >
        <div className="flex items-center gap-3">
          <ChevronRight
            className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
          />
          <div className="text-left">
            <div className="font-semibold text-sm">{label}</div>
            <div className="text-xs text-muted-foreground">
              {batch.count} transaction{batch.count === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <div className="num font-semibold">{fmtCurrency((batch.net || 0) / 100)}</div>
      </button>
      {open && (
        <div className="pb-2 -mx-6 md:mx-0 mt-1">
          <PayoutBreakdown availableOn={batch.available_on} />
        </div>
      )}
    </div>
  );
}

function ExpandablePayoutRow({ p, highlight, failed }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`recent-payout-row-${p.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left hover:bg-secondary/30 -mx-2 px-2 rounded transition-colors flex items-start gap-2"
      >
        <ChevronRight
          className={`w-4 h-4 text-muted-foreground mt-4 transition-transform shrink-0 ${open ? "rotate-90" : ""}`}
        />
        <div className="flex-1">
          <PayoutRow p={p} highlight={highlight} failed={failed} />
        </div>
      </button>
      {open && (
        <div className="pb-2 mt-1">
          <PayoutBreakdown payoutId={p.id} />
        </div>
      )}
    </div>
  );
}

function PayoutRow({ p, highlight, failed }) {
  const arrived = p.arrival_date ? new Date(p.arrival_date * 1000) : null;
  const arrLabel = arrived ? arrived.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const isBank = p.type === "bank_account";
  const dest = p.destination ? p.destination.replace(/^ba_|^card_/, "") : "";
  const statusTone = failed
    ? "bg-destructive/10 text-destructive border-destructive/30"
    : highlight
      ? "bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40"
      : "bg-primary/10 text-primary border-primary/30";
  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm num">{fmtCurrency((p.amount || 0) / 100)}</span>
          <span className={`pill ${statusTone} capitalize`}>{(p.status || "").replace(/_/g, " ")}</span>
          {p.automatic && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">auto</span>}
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {isBank ? "Bank" : "Card"} {dest && <span className="font-mono">•••• {dest.slice(-4)}</span>}
          {p.method === "instant" && <span className="ml-2 text-[#a44231]">Instant</span>}
          {p.failure_message && <span className="ml-2 text-destructive">· {p.failure_message}</span>}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-medium">{arrLabel}</div>
        <div className="text-[11px] text-muted-foreground">
          {highlight ? "Arriving" : failed ? "Failed" : "Arrived"}
        </div>
      </div>
    </div>
  );
}
